/**
 * variant-geo-filter.js
 *
 * Скрывает продукты и варианты продукта в зависимости от страны пользователя.
 *
 * Логика:
 *  - Если `usHide: true`     → скрыто для покупателей из США.
 *  - Если `canadaHide: true` → скрыто для покупателей из Канады.
 *  - Если `ukHide: true`     → скрыто для покупателей из Великобритании.
 *  - Если `europeHide: true` → скрыто для покупателей из стран ЕС.
 *  - Если `restHide: true`   → скрыто для всех остальных покупателей.
 *  - Все флаги независимы и могут быть установлены одновременно.
 *
 * Поддерживаемые места отображения:
 *  A) <variant-picker> — страница продукта, quick view, featured product
 *     - color swatch    (product-variant-picker-swatch.liquid)
 *     - label/button    (product-variant-picker-button.liquid)
 *     - variant image   (product-variant-picker-variant.liquid)
 *     - dropdown select (product-variant-picker-dropdown.liquid)
 *
 *  B) product-card — страницы коллекций, поиск, виджеты
 *     - свотчи рендерятся напрямую в Liquid, вне <variant-picker>
 *     - идентифицируются по [data-product-swatches] на контейнере
 *     - каждый <li data-geo-swatch data-option-value="...">
 *
 * Данные о метафилдах вставляются сниппетом `variant-geo-data.liquid`
 * в виде <script type="application/json" data-geo-variants>
 * с полями { id, option1, option2, option3, usHide, canadaHide, ukHide, europeHide, restHide }.
 */

(function () {
  // ── Утилиты ──────────────────────────────────────────────────────────────

  const log = (...args) => console.log('[variant-geo-filter]', ...args);
  let resolvedCountryCode = null;

  function normalizeCountryCode(countryCode) {
    const code = String(countryCode || '').trim().toUpperCase();
    return code === 'NULL' || code === 'UNDEFINED' ? '' : code;
  }

  function readGeo() {
    try { return normalizeCountryCode(localStorage.getItem('userGeolocation')); } catch (_) { return ''; }
  }

  const EU_COUNTRY_CODES = new Set([
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
    'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
    'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  ]);

  function getGeoRegion(countryCode) {
    const code = normalizeCountryCode(countryCode);
    if (code === 'US') return 'US';
    if (code === 'CA') return 'CANADA';
    if (code === 'GB' || code === 'UK') return 'UK';
    if (EU_COUNTRY_CODES.has(code)) return 'EUROPE';
    return 'REST';
  }

  function normalizeFlag(value) {
    if (value === true || value === 1) return true;
    const normalized = String(value || '').trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }

  function getResolvedCountryCode() {
    const current = readGeo();
    if (current && current !== resolvedCountryCode) {
      resolvedCountryCode = current;
    }
    if (resolvedCountryCode !== null) return resolvedCountryCode;
    return current || null;
  }

  /** Debounce: вызывает fn не чаще раза в wait мс */
  function debounce(fn, wait = 150) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  }

  /**
   * Парсит JSON из <script data-geo-variants>.
   * @param {HTMLElement} container
   * @returns {Array|null}
   */
  function parseGeoRules(container) {
    const el = container.querySelector('[data-geo-variants]');
    if (!el) return null;
    try {
      const rules = JSON.parse(el.textContent);
      return Array.isArray(rules) && rules.length > 0 ? rules : null;
    } catch (_) {
      log('Invalid data-geo-variants JSON');
      return null;
    }
  }

  function parseProductGeoRules(container) {
    const el = container.querySelector('[data-geo-product-variants]');
    if (!el) return null;
    try {
      const rules = JSON.parse(el.textContent);
      return Array.isArray(rules) && rules.length > 0 ? rules : null;
    } catch (_) {
      log('Invalid data-geo-product-variants JSON');
      return null;
    }
  }

  // ── Общая логика ─────────────────────────────────────────────────────────

  /**
   * Возвращает Set ID вариантов, которые нужно скрыть для переданного кода страны.
   * @param {Array} geoRules  - массив { id, usHide, canadaHide, ukHide, europeHide, restHide, ... }
   * @param {string} countryCode
   * @returns {Set<number>}
   */
  function buildHiddenVariantIds(geoRules, countryCode) {
    const region = getGeoRegion(countryCode);
    const hidden = new Set();

    geoRules.forEach(({ id, usHide, canadaHide, ukHide, europeHide, restHide }) => {
      if (region === 'US' && normalizeFlag(usHide)) hidden.add(id);
      if (region === 'CANADA' && normalizeFlag(canadaHide)) hidden.add(id);
      if (region === 'UK' && normalizeFlag(ukHide)) hidden.add(id);
      if (region === 'EUROPE' && normalizeFlag(europeHide)) hidden.add(id);
      if (region === 'REST' && normalizeFlag(restHide)) hidden.add(id);
    });

    return hidden;
  }

  function areAllVariantsHidden(geoRules, hiddenIds) {
    return geoRules.length > 0 && geoRules.every((rule) => hiddenIds.has(rule.id));
  }

  function getRegionFlagValue(source, region) {
    if (!source) return false;

    if (region === 'US') return source.geoUsHide ?? source.usHide;
    if (region === 'CANADA') return source.geoCanadaHide ?? source.canadaHide;
    if (region === 'UK') return source.geoUkHide ?? source.ukHide;
    if (region === 'EUROPE') return source.geoEuropeHide ?? source.europeHide;
    return source.geoRestHide ?? source.restHide;
  }

  function ruleIsHiddenForRegion(source, countryCode) {
    return normalizeFlag(getRegionFlagValue(source, getGeoRegion(countryCode)));
  }

  function getVariantRulesForElement(element) {
    return parseGeoRules(element) || parseProductGeoRules(element);
  }

  function areElementVariantsHidden(element, countryCode) {
    const geoRules = getVariantRulesForElement(element);
    if (!geoRules) return false;

    const hiddenIds = buildHiddenVariantIds(geoRules, countryCode);
    return areAllVariantsHidden(geoRules, hiddenIds);
  }

  function getProductInfo(picker) {
    return picker.closest('product-info');
  }

  function getProductForm(productInfo) {
    if (!productInfo) return null;
    const formId = productInfo.getAttribute('form');
    return formId ? document.forms[formId] : null;
  }

  function getFormSubmitButton(form) {
    return form?.querySelector('[type="submit"]');
  }

  function getButtonTextElement(button) {
    return button?.querySelector('.btn-text>span') || button?.querySelector('.btn-text') || button;
  }

  function rememberButtonState(button) {
    if (!button || button.dataset.geoOriginalText !== undefined) return;

    const text = getButtonTextElement(button);
    button.dataset.geoOriginalText = text?.textContent || '';
    button.dataset.geoOriginalHidden = button.hasAttribute('hidden') ? 'true' : 'false';
    button.dataset.geoOriginalDisabled = button.hasAttribute('disabled') ? 'true' : 'false';
    button.dataset.geoOriginalUnavailable = button.hasAttribute('unavailable') ? 'true' : 'false';
  }

  function restoreButtonState(button) {
    if (!button || button.dataset.geoOriginalText === undefined) return;

    const text = getButtonTextElement(button);
    button.toggleAttribute('hidden', button.dataset.geoOriginalHidden === 'true');
    button.toggleAttribute('disabled', button.dataset.geoOriginalDisabled === 'true');
    button.toggleAttribute('unavailable', button.dataset.geoOriginalUnavailable === 'true');
    if (text) text.textContent = button.dataset.geoOriginalText;
  }

  function setPaymentButtonsHidden(form, hidden) {
    form?.querySelectorAll('.shopify-payment-button').forEach((button) => {
      if (button.dataset.geoOriginalHidden === undefined) {
        button.dataset.geoOriginalHidden = button.hasAttribute('hidden') ? 'true' : 'false';
      }

      button.toggleAttribute('hidden', hidden || button.dataset.geoOriginalHidden === 'true');
    });
  }

  function getInventoryElement(productInfo) {
    if (!productInfo) return null;

    const sectionId = productInfo.getAttribute('data-original-section-id') || productInfo.getAttribute('data-section-id');
    const productId = productInfo.getAttribute('data-product-id');
    return (
      productInfo.querySelector('.product__inventory') ||
      (sectionId && productId ? document.getElementById(`Inventory-${sectionId}-${productId}`) : null)
    );
  }

  function setInventoryHidden(productInfo, hidden) {
    const inventory = getInventoryElement(productInfo);
    if (!inventory) return;

    if (inventory.dataset.geoOriginalHidden === undefined) {
      inventory.dataset.geoOriginalHidden = inventory.hasAttribute('hidden') ? 'true' : 'false';
    }

    inventory.toggleAttribute('hidden', hidden || inventory.dataset.geoOriginalHidden === 'true');
    inventory.toggleAttribute('data-geo-inventory-hidden', hidden);
  }

  function getStickyForm(productInfo) {
    if (!productInfo) return null;
    const sectionId = productInfo.getAttribute('data-original-section-id') || productInfo.getAttribute('data-section-id');
    const productId = productInfo.getAttribute('data-product-id');
    return sectionId && productId ? document.getElementById(`ProductStickyForm-${sectionId}-${productId}`) : null;
  }

  function applyProductAvailabilityState(productInfo, allVariantsHidden, picker = null) {
    const productForm = getProductForm(productInfo);
    const stickyForm = getStickyForm(productInfo);
    const variantIdInput = productForm?.querySelector('[name="id"]');

    picker?.toggleAttribute('data-geo-all-hidden', allVariantsHidden);
    productInfo?.toggleAttribute('data-geo-all-hidden', allVariantsHidden);
    productForm?.toggleAttribute('data-geo-all-hidden', allVariantsHidden);
    setInventoryHidden(productInfo, allVariantsHidden);

    if (!allVariantsHidden) {
      if (variantIdInput) variantIdInput.disabled = false;
      return;
    }

    if (variantIdInput) {
      variantIdInput.value = '';
      variantIdInput.disabled = true;
      variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    productForm?.handleErrorMessage?.();
    productForm?.toggleSubmitButton?.(true, theme.variantStrings.soldOut, true);
    stickyForm?.handleErrorMessage?.();
    stickyForm?.toggleSubmitButton?.(true, theme.variantStrings.soldOut);
  }

  /**
   * Проверяет, должно ли данное значение опции быть скрыто.
   * Скрываем, только если ВСЕ варианты с этим значением опции гео-заблокированы.
   *
   * @param {Array}  geoRules  - [{id, option1, option2, option3, usHide, canadaHide, ukHide, europeHide, restHide}]
   * @param {Set}    hiddenIds - Set скрытых variant ID
   * @param {string} optionKey - "option1" | "option2" | "option3"
   * @param {string} optionValue - значение опции (e.g. "Red")
   * @returns {boolean}
   */
  function shouldHideOptionValue(geoRules, hiddenIds, optionKey, optionValue) {
    const matching = geoRules.filter((r) => r[optionKey] === optionValue);
    return matching.length > 0 && matching.every((r) => hiddenIds.has(r.id));
  }

  // ── A. Фильтр для <variant-picker> ──────────────────────────────────────

  /**
   * Применяет гео-фильтр к одному элементу <variant-picker>.
   * @param {HTMLElement} picker
   * @param {string} countryCode
   * @param {{ skipAutoSwitch?: boolean, skipAvailabilityState?: boolean }} [options]
   */
  function applyGeoFilterToPicker(picker, countryCode, options = {}) {
    const geoRules = parseGeoRules(picker);
    if (!geoRules) return;

    // Полный список вариантов продукта (содержит option1, option2, option3).
    // Начиная с этого PR, option1/2/3 также есть в geoRules, поэтому
    // для сопоставления опций мы используем geoRules напрямую.
    // data-variants по-прежнему нужен теме для её собственной логики.
    const productInfo = getProductInfo(picker);
    const wholeProductHidden = shouldHideProduct(productInfo, countryCode);
    const hiddenIds = wholeProductHidden
      ? new Set(geoRules.map((rule) => rule.id))
      : buildHiddenVariantIds(geoRules, countryCode);
    const allVariantsHidden = wholeProductHidden || areAllVariantsHidden(geoRules, hiddenIds);

    // ── 1. Radio-пикеры (swatch, button, variant-image) ──────────────────

    picker.querySelectorAll('fieldset[data-option-index]').forEach((fieldset) => {
      // data-option-index = "option1" | "option2" | "option3"
      const optionKey = fieldset.dataset.optionIndex;

      fieldset.querySelectorAll('li').forEach((li) => {
        const input = li.querySelector('input[type="radio"]');
        if (!input) return;

        const hide = shouldHideOptionValue(geoRules, hiddenIds, optionKey, input.value);

        if (hide) {
          li.hidden = true;
          input.disabled = true;
        } else {
          li.hidden = false;
          // Не снимаем disabled если он выставлен темой (sold-out)
          if (!input.classList.contains('disabled')) {
            input.disabled = false;
          }
        }
      });
    });

    // ── 2. Dropdown-пикеры (<select>) ────────────────────────────────────

    picker.querySelectorAll('[data-option-index] select').forEach((select) => {
      const wrapper = select.closest('[data-option-index]');
      const optionKey = wrapper?.dataset.optionIndex;
      if (!optionKey) return;

      Array.from(select.options).forEach((option) => {
        const hide = shouldHideOptionValue(geoRules, hiddenIds, optionKey, option.value);

        option.hidden = hide;
        if (hide) {
          option.disabled = true;
        } else if (!option.dataset.soldOut) {
          option.disabled = false;
        }
      });
    });

    // ── 3. Автопереключение на первый видимый вариант ────────────────────
    if (!options.skipAutoSwitch) {
      autoSwitchToVisibleOption(picker);
    }
    if (options.skipAvailabilityState) {
      picker.toggleAttribute('data-geo-all-hidden', allVariantsHidden);
      return;
    }

    applyProductAvailabilityState(productInfo, allVariantsHidden, picker);
  }

  function applyGeoAvailabilityToProductInfo(productInfo, countryCode) {
    if (shouldHideProduct(productInfo, countryCode)) {
      applyProductAvailabilityState(productInfo, true);
      productInfo.removeAttribute('data-geo-pending');
      productInfo.setAttribute('data-geo-resolved', '');
      return;
    }

    if (productInfo.querySelector('variant-picker[id]')) {
      productInfo.removeAttribute('data-geo-pending');
      productInfo.setAttribute('data-geo-resolved', '');
      return;
    }

    const geoRules = parseProductGeoRules(productInfo);
    if (!geoRules) {
      productInfo.removeAttribute('data-geo-pending');
      productInfo.setAttribute('data-geo-resolved', '');
      return;
    }

    const hiddenIds = buildHiddenVariantIds(geoRules, countryCode);
    applyProductAvailabilityState(productInfo, areAllVariantsHidden(geoRules, hiddenIds));
    productInfo.removeAttribute('data-geo-pending');
    productInfo.setAttribute('data-geo-resolved', '');
  }

  function applyGeoAvailabilityToDirectForms(countryCode) {
    document.querySelectorAll('form[is="product-form"]').forEach((form) => {
      const geoRules = getVariantRulesForElement(form);
      if (!geoRules) return;

      const productContainer = form.closest('product-info, .product-card, .horizontal-product');
      const wholeProductHidden = shouldHideProduct(productContainer, countryCode);
      const hiddenIds = wholeProductHidden
        ? new Set(geoRules.map((rule) => rule.id))
        : buildHiddenVariantIds(geoRules, countryCode);
      const allVariantsHidden = wholeProductHidden || areAllVariantsHidden(geoRules, hiddenIds);
      const variantIdInput = form.querySelector('[name="id"]');
      const submitButton = getFormSubmitButton(form);
      const customOutOfStock = submitButton?.hasAttribute('data-custom-out-of-stock');
      rememberButtonState(submitButton);

      form.toggleAttribute('data-geo-all-hidden', allVariantsHidden);
      form.hidden = false;

      if (!allVariantsHidden) {
        if (variantIdInput) {
          variantIdInput.disabled = false;
          if (!variantIdInput.value && submitButton?.dataset.variantId) {
            variantIdInput.value = submitButton.dataset.variantId;
          }
        }
        restoreButtonState(submitButton);
        submitButton?.toggleAttribute('hidden', customOutOfStock);
        setPaymentButtonsHidden(form, false);
        return;
      }

      if (variantIdInput) {
        variantIdInput.value = '';
        variantIdInput.disabled = true;
        variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      form.handleErrorMessage?.();
      const unavailableText = theme.variantStrings.outOfStock || theme.variantStrings.soldOut;
      if (form.toggleSubmitButton) {
        form.toggleSubmitButton(true, unavailableText, true);
      } else if (submitButton) {
        const text = getButtonTextElement(submitButton);
        submitButton.setAttribute('disabled', '');
        submitButton.setAttribute('unavailable', '');
        text.textContent = unavailableText;
      }

      submitButton?.removeAttribute('hidden');
      setPaymentButtonsHidden(form, true);
    });
  }

  /**
   * Если текущий выбранный вариант скрыт — переключаемся на первый видимый.
   * Диспатчим `change`, чтобы тема обновила цену, галерею, ATC-кнопку и URL.
   */
  function autoSwitchToVisibleOption(picker) {
    // Radio fieldsets
    picker.querySelectorAll('fieldset[data-option-index]').forEach((fieldset) => {
      const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
      if (!checkedInput) return;

      const parentLi = checkedInput.closest('li');
      if (!parentLi || !parentLi.hidden) return;

      const firstVisible = Array.from(
        fieldset.querySelectorAll('li:not([hidden]) input[type="radio"]')
      ).find((i) => !i.disabled);

      if (firstVisible) {
        firstVisible.checked = true;
        // Запускаем цепочку обновлений темы (цена, галерея, ATC, URL)
        firstVisible.dispatchEvent(new Event('change', { bubbles: true }));
        log('Picker: auto-switched to', firstVisible.value);
      }
    });

    // Select dropdowns
    picker.querySelectorAll('[data-option-index] select').forEach((select) => {
      const selected = select.selectedOptions[0];
      if (!selected || !selected.hidden) return;

      const firstVisible = Array.from(select.options).find(
        (o) => !o.hidden && !o.disabled
      );

      if (firstVisible) {
        select.value = firstVisible.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        log('Dropdown: auto-switched to', firstVisible.value);
      }
    });
  }

  // ── B. Фильтр для карточек коллекции [data-product-swatches] ────────────

  /**
   * Применяет гео-фильтр к карточкам коллекции.
   * Свотчи на карточках рендерятся прямо в Liquid, вне <variant-picker>.
   * Каждый контейнер [data-product-swatches] содержит:
   *   - <script data-geo-variants>   — гео-правила с option1/2/3
   *   - <li data-geo-swatch data-option-value="..."> — каждый свотч
   *   - data-option-key="option1"    — на самом контейнере
   *
   * @param {string} countryCode
   */
  function applyGeoFilterToCardSwatches(countryCode) {
    document.querySelectorAll('[data-product-swatches]').forEach((container) => {
      const geoRules = parseGeoRules(container);
      if (!geoRules) return;

      const optionKey = container.dataset.optionKey; // "option1", "option2", etc.
      if (!optionKey) return;

      const hiddenIds = buildHiddenVariantIds(geoRules, countryCode);

      // ── Общее количество гео-доступных цветов ────────────────────────
      // Из data-geo-variants получаем ВСЕ уникальные значения опции,
      // включая те, что не вошли в DOM из-за лимита max_color_count.
      // Это даёт нам правильный знаменатель для счётчика "+N".
      const allUniqueValues = [...new Set(geoRules.map((r) => r[optionKey]))];
      const totalGeoVisible = allUniqueValues.filter(
        (val) => !shouldHideOptionValue(geoRules, hiddenIds, optionKey, val)
      ).length;

      // ── Скрываем свотчи в DOM ─────────────────────────────────────────
      let renderedVisible = 0;

      container.querySelectorAll('li[data-geo-swatch]').forEach((li) => {
        const optionValue = li.dataset.optionValue;
        const hide = shouldHideOptionValue(geoRules, hiddenIds, optionKey, optionValue);

        li.hidden = hide;
        if (!hide) renderedVisible++;
      });

      // Если ни одного свотча не осталось — скрываем весь блок
      container.hidden = renderedVisible === 0;

      // ── Пересчёт счётчика "+N" ────────────────────────────────────────
      // remaining = (все гео-доступные) − (те, что показаны в DOM)
      // Если > 0 — обновляем текст; если ≤ 0 — скрываем ссылку.
      const moreLink = container.querySelector('.js-geo-more-count');
      if (moreLink) {
        const remaining = totalGeoVisible - renderedVisible;
        if (remaining > 0) {
          moreLink.textContent = '+' + remaining;
          moreLink.hidden = false;
        } else {
          moreLink.hidden = true;
        }
      }
    });
  }

  // ── C. Фильтр для целых продуктов [data-geo-product] ───────────────────

  function shouldHideProduct(element, countryCode) {
    if (!element) return false;
    return ruleIsHiddenForRegion(element.dataset || {}, countryCode);
  }

  function removeControlledQuickView(element) {
    const controls = element.getAttribute('aria-controls');
    if (!controls) return;

    document.getElementById(controls)?.remove();
  }

  function markProductContainers(element) {
    element.closest('motion-list, product-complementary, recently-viewed, product-recommendations, slider-element, carousel-element')?.setAttribute('data-geo-had-products', '');
    element.closest('.recommendations-section, .recently-section')?.setAttribute('data-geo-had-products', '');
  }

  function getVisibleProductCards(container) {
    return Array.from(
      container.querySelectorAll('.product-card:not(.product-card--promo):not(.article-card), .horizontal-product')
    ).filter((item) => item.isConnected && item.clientWidth > 0 && item.clientHeight > 0);
  }

  function refreshSliderElement(slider) {
    slider.reset?.();
    slider.updateButtons?.();
    slider.updateTabindex?.();
  }

  function refreshProductComplementary(container) {
    const currentIndex = container.carousel?.slider?.selectedIndex ?? container.selectedIndex;
    const isEmpty = getVisibleProductCards(container).length === 0;
    let selectedIndex = currentIndex;

    if (container.carousel?.slider) {
      container.carousel.slider.reloadCells?.();
      container.carousel.slider.resize?.();
      const slideCount = container.carousel.slider.slides?.length || getVisibleProductCards(container).length;
      selectedIndex = Math.min(currentIndex, Math.max(slideCount - 1, 0));
      if (!isEmpty) container.carousel.slider.select?.(selectedIndex, false, true);
    }

    if (!isEmpty) container.selectedIndex = selectedIndex;
    container.closest('product-recommendations')?.toggleAttribute('hidden', isEmpty);
  }

  function refreshHorizontalProductGroups() {
    document.querySelectorAll('.horizontal-products').forEach((group) => {
      const isEmpty = getVisibleProductCards(group).length === 0;
      group.hidden = isEmpty;

      const headingGroup = group.closest('.grid');
      if (headingGroup && headingGroup.querySelector('.search__heading')) {
        headingGroup.hidden = isEmpty;
      }
    });
  }

  function ensureGeoEmptyState(container) {
    if (!container.hasAttribute('data-geo-had-products')) return;

    const visibleProducts = getVisibleProductCards(container);
    const existing = container.querySelector(':scope > [data-geo-empty-state]');

    if (visibleProducts.length > 0) {
      existing?.remove();
      container.hidden = false;
      return;
    }

    if (existing) return;

    const empty = document.createElement('div');
    empty.className = 'empty-state grid items-center justify-center text-center gap-7d5';
    empty.setAttribute('data-geo-empty-state', '');
    empty.innerHTML = '<p class="heading leading-none tracking-tight">No products available in your region.</p>';
    container.appendChild(empty);
  }

  function refreshProductDiscoveryUI() {
    document.querySelectorAll('slider-element').forEach(refreshSliderElement);
    document.querySelectorAll('motion-list[data-geo-had-products], #ProductGridContainer[data-geo-had-products]').forEach(ensureGeoEmptyState);
    document.querySelectorAll('product-complementary').forEach(refreshProductComplementary);
    refreshHorizontalProductGroups();

    document.querySelectorAll('recently-viewed[data-geo-had-products]').forEach((container) => {
      if (getVisibleProductCards(container).length === 0) {
        container.dispatchEvent(new CustomEvent('is-empty', { bubbles: true }));
        container.closest('.recently-section')?.remove();
      }
    });
  }

  function applyGeoFilterToProducts(countryCode) {
    document.querySelectorAll('[data-geo-product]:not(product-info), .product-card, .horizontal-product').forEach((element) => {
      markProductContainers(element);

      const hide = shouldHideProduct(element, countryCode);

      if (hide) {
        element.setAttribute('data-geo-hidden', '');
        removeControlledQuickView(element);
        element.remove();
        return;
      }

      element.removeAttribute('data-geo-pending');
      element.removeAttribute('data-geo-hidden');
      element.setAttribute('data-geo-resolved', '');
    });

    refreshProductDiscoveryUI();
  }

  // ── Объединённый запуск ───────────────────────────────────────────────────

  /**
   * Применяет гео-фильтр ко всем элементам на странице:
   *  - <variant-picker> (страница продукта, quick view)
   *  - [data-product-swatches] (карточки коллекции)
   */
  function applyGeoFilterToAll(countryCode) {
    // A. Whole product cards and product discovery controls
    applyGeoFilterToProducts(countryCode);

    // A. Variant pickers
    document.querySelectorAll('variant-picker[id]').forEach((picker) => {
      applyGeoFilterToPicker(picker, countryCode);
    });

    document.querySelectorAll('product-info').forEach((productInfo) => {
      applyGeoAvailabilityToProductInfo(productInfo, countryCode);
    });

    applyGeoAvailabilityToDirectForms(countryCode);

    // B. Collection card swatches
    applyGeoFilterToCardSwatches(countryCode);
  }

  window.variantGeoFilter = {
    ...(window.variantGeoFilter || {}),
    applyToPicker(picker, options = {}) {
      const code = options.countryCode || readGeo();
      if (!picker || !code) return false;

      applyGeoFilterToPicker(picker, code, options);
      return true;
    },
    applyToAll(countryCode = readGeo()) {
      if (!countryCode) return false;

      applyGeoFilterToAll(countryCode);
      return true;
    },
    getRegion: getGeoRegion,
    productIsHidden(element, countryCode = readGeo()) {
      return Boolean(element && shouldHideProduct(element, countryCode));
    },
  };

  // ── Инициализация ─────────────────────────────────────────────────────────

  const debouncedApply = debounce((code) => applyGeoFilterToAll(code), 100);

  /**
   * Запускает фильтрацию как только геолокация известна.
   * Если geo уже в localStorage — применяем сразу, иначе ждём geo:ready.
   */
  function init() {
    const cached = readGeo();

    if (cached) {
      resolvedCountryCode = cached;
      log('Geo from cache:', cached);
      applyGeoFilterToAll(cached);
    }

    // Первый визит: ждём, пока geo-detect.js получит код страны
    document.addEventListener('geo:ready', (e) => {
      const code = normalizeCountryCode(e.detail?.countryCode || readGeo() || '');
      resolvedCountryCode = code;
      log('geo:ready event, code:', code);
      applyGeoFilterToAll(code);
    });

    // Перерисовка секций в редакторе Shopify
    document.addEventListener('shopify:section:load', () => {
      const code = getResolvedCountryCode();
      if (code !== null) applyGeoFilterToAll(code);
    });

    ['collection:reloaded', 'recommendations:loaded', 'recentlyViewed:loaded', 'quickview:loaded'].forEach((eventName) => {
      document.addEventListener(eventName, () => {
        const code = getResolvedCountryCode();
        if (code !== null) applyGeoFilterToAll(code);
      });
    });

    // После изменения варианта (перестройка опций темой) — перепроверяем скрытие
    document.addEventListener('variant:change', () => {
      const code = getResolvedCountryCode();
      if (code !== null) debouncedApply(code);
    }, { capture: true });

    // MutationObserver: отслеживаем появление новых picker/карточек
    // (quick-view, infinite scroll, lazy-render)
    const observer = new MutationObserver(
      debounce(() => {
        const code = getResolvedCountryCode();
        if (code !== null) debouncedApply(code);
      }, 200)
    );

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
  
